import { Box, TextField, InputBase, IconButton, Button, Tooltip } from "@mui/material";
import {
  SmileIcon,
  PaperclipIcon,
  SendIcon,
  ImageIcon,
  FileIcon,
  LinkIcon,
  WifiOffIcon,
} from "lucide-react";
import React, { useState } from "react";

import { useOmniChat } from "0.Mine/1.Hooks/CRM/omnichat";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";

interface MessageComposerProps {
  channel: string;
  addMessage: (message: string) => void;
  conversationId: string;
  recipient: string;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  channel,
  addMessage,
  conversationId,
  recipient,
}) => {
  const [message, setMessage] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const isEmail = channel === "email";
  const [subject, setSubject] = useState("");
  const { session } = useBusinessContext();
  const { sendSocialMediaMessage, replyToReview, sendWhatsappMessage, isConnected, wsError } =
    useOmniChat(session.selectedBusiness?.id?.toString());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      if (!session.selectedBusiness.id) return;

      let response;

      switch (channel) {
        case "gmb":
          response = await replyToReview(session.selectedBusiness.id, conversationId, message);
          break;
        case "whatsapp":
          response = await sendWhatsappMessage(
            session.selectedBusiness.id,
            conversationId,
            message
          );
          break;
        default:
          response = await sendSocialMediaMessage(
            session.selectedBusiness.id,
            channel,
            conversationId,
            message,
            recipient
          );
          break;
      }

      if (response?.success) {
        setMessage("");
        if (isEmail) setSubject("");
        addMessage(message);
      }
    }
  };

  return (
    <Box sx={{ backgroundColor: "white", borderTop: "1px solid #E5E7EB", p: 2 }}>
      {isEmail && (
        <TextField
          fullWidth
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
      )}
      <Box component="form" onSubmit={handleSubmit} sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F3F4F6",
            borderRadius: "8px",
            px: 1.5,
            py: 1,
          }}
        >
          <IconButton type="button" sx={{ color: "#6B7280", mr: 1 }}>
            <SmileIcon size={20} />
          </IconButton>
          <Box sx={{ position: "relative" }}>
            <IconButton
              type="button"
              sx={{ color: "#6B7280", mr: 1 }}
              onClick={() => setShowAttachMenu(!showAttachMenu)}
            >
              <PaperclipIcon size={20} />
            </IconButton>
            {showAttachMenu && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  mb: 1,
                  backgroundColor: "white",
                  borderRadius: "8px",
                  boxShadow: 1,
                  border: "1px solid #E5E7EB",
                  py: 1,
                  zIndex: 1,
                }}
              >
                <Button
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    px: 2,
                    py: 1,
                    fontSize: "0.875rem",
                    color: "inherit",
                    "&:hover": { backgroundColor: "#F3F4F6" },
                  }}
                >
                  <ImageIcon size={16} style={{ marginRight: 8 }} />
                  Image
                </Button>
                <Button
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    px: 2,
                    py: 1,
                    fontSize: "0.875rem",
                    color: "inherit",
                    "&:hover": { backgroundColor: "#F3F4F6" },
                  }}
                >
                  <FileIcon size={16} style={{ marginRight: 8 }} />
                  Document
                </Button>
                <Button
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    px: 2,
                    py: 1,
                    fontSize: "0.875rem",
                    color: "inherit",
                    "&:hover": { backgroundColor: "#F3F4F6" },
                  }}
                >
                  <LinkIcon size={16} style={{ marginRight: 8 }} />
                  Link
                </Button>
              </Box>
            )}
          </Box>
          <InputBase
            placeholder={`Type a ${isEmail ? "message" : channel} message...`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{ flex: 1, mx: 2 }}
          />
          {!isConnected && (
            <Tooltip title={wsError || "Offline mode - messages will be sent via HTTP"}>
              <IconButton
                sx={{
                  color: "#EF4444",
                  mr: 1,
                }}
              >
                <WifiOffIcon size={18} />
              </IconButton>
            </Tooltip>
          )}
          {message.trim() ? (
            <IconButton
              type="submit"
              sx={{
                backgroundColor: "#3B82F6",
                color: "white",
                borderRadius: "50%",
                p: 1,
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              <SendIcon size={18} />
            </IconButton>
          ) : (
            <IconButton type="button" sx={{ color: "#6B7280" }}>
              <Box sx={{ width: 20, height: 20 }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageComposer;
